/**
 * DWG 估價測試（室內設計服務中心 — Sprint 0 唯一業務頁）
 * 上傳 DWG/DXF → 後端轉檔解析套規則 → Table 預覽 → 下載 Excel。
 * func_code: ee_inhouse_dwg_test
 */
import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../contexts/MessageContext';
import { usePermission } from '../hooks/usePermission';
import DwgUploadForm from '../components/interiorDesign/DwgUploadForm';
import {
  uploadDwg, getDwgPreview, downloadDwgExcel, QuoteItem,
} from '../services/interiorDesignService';

const FUNC_CODE = 'ee_inhouse_dwg_test';

const DwgTestPage: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission, loading: permLoading } = usePermission();

  const canRead = hasPermission(FUNC_CODE, 'read');
  const canCreate = hasPermission(FUNC_CODE, 'create');
  const canExport = hasPermission(FUNC_CODE, 'print');

  const [uploading, setUploading] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [warnings, setWarnings] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const { session_id, items_count } = await uploadDwg(file);
      showSuccess('SYS340002', { name: `${items_count}` });
      const preview = await getDwgPreview(session_id);
      setSessionId(session_id);
      setItems(preview.items);
      setWarnings(preview.meta.parse_warnings ?? null);
    } catch (err) {
      showApiError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    if (sessionId == null) return;
    try {
      await downloadDwgExcel(sessionId);
      showSuccess('SYS340003', { name: `#${sessionId}` });
    } catch (err) {
      showApiError(err);
    }
  };

  if (!permLoading && !canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('common.noPermission', '無權限')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('interiorDesign.dwgTest')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('interiorDesign.dwgTestDesc')}
      </Typography>

      {canCreate && (
        <Box sx={{ mb: 3 }}>
          <DwgUploadForm onUpload={handleUpload} uploading={uploading} />
        </Box>
      )}

      {warnings && (
        <Alert severity="info" sx={{ mb: 2 }}>{warnings}</Alert>
      )}

      {items.length > 0 ? (
        <Paper variant="outlined">
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip label={`${t('interiorDesign.itemsCount')}: ${items.length}`} color="primary" />
            {canExport && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                sx={{ ml: 'auto' }}
              >
                {t('interiorDesign.exportExcel')}
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('interiorDesign.colCategory')}</TableCell>
                  <TableCell>{t('interiorDesign.colLayer')}</TableCell>
                  <TableCell>{t('interiorDesign.colName')}</TableCell>
                  <TableCell align="right">{t('interiorDesign.colQty')}</TableCell>
                  <TableCell>{t('interiorDesign.colUnit')}</TableCell>
                  <TableCell>{t('interiorDesign.colMethod')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{it.category} {it.cat_name}</TableCell>
                    <TableCell>{it.layer}</TableCell>
                    <TableCell>{it.budget_name}</TableCell>
                    <TableCell align="right">{it.qty}</TableCell>
                    <TableCell>{it.unit}</TableCell>
                    <TableCell>{it.method}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Typography color="text.secondary">{t('interiorDesign.empty')}</Typography>
      )}
    </Box>
  );
};

export default DwgTestPage;
